import type { ApiError } from '@diagon-alley/common';

/**
 * Extracts a user-readable message from anything thrown by ReparoApi or
 * its callers. ReparoApi rejects with an `ApiError` shape
 * `{ status, error: { message, ... } }`, NOT a JS Error — so the common
 * `err instanceof Error ? err.message : String(err)` pattern produces
 * literally `[object Object]` for the most common failure mode.
 *
 * Resolution order:
 *   1. ApiError shape (err.error.message) — the standard Reparo backend reject
 *   2. Error instance (err.message) — fetch failures, validation throws, etc.
 *   3. Plain object with .message — third-party SDKs
 *   4. String error — historical / cross-realm
 *   5. Final fallback message — never returns `[object Object]`
 */
export function extractApiErrorMessage(
    err: unknown,
    fallback = 'Something went wrong. Please try again.',
): string {
    if (err && typeof err === 'object') {
        const apiErr = err as Partial<ApiError>;
        if (apiErr.error?.message) return apiErr.error.message;
    }
    if (err instanceof Error && err.message) return err.message;
    if (err && typeof err === 'object') {
        const direct = (err as { message?: unknown }).message;
        if (typeof direct === 'string' && direct.length > 0) return direct;
    }
    if (typeof err === 'string' && err.length > 0) return err;
    return fallback;
}
